import React, { createContext, useContext, useEffect, useRef } from 'react';
import { View } from 'react-native-web';
import { usePlayedVideo } from 'system/videotime';
import { useDatastore, useSessionData } from 'util/datastore';
export type VideoPlayerState = {
    paused: boolean; // Indicates if the video is currently paused
    currentTime: number; // The current playback time of the video in seconds.
    duration: number; // The total duration of the video in seconds.
    ended: boolean; // Whether the video has ended
    src: string; // The URL of the video.
    ready: boolean; // The URL of the video.
    play: () => void; // Function to start the video playback.
    pause: () => void; // Function to pause the video playback.
    seek: (time: number) => void; // Function to change the current time of the video.
};

// Custom VideoContext. It should provide all necessary data from a played video
export const VideoPlayerStateContext = createContext<VideoPlayerState>({} as VideoPlayerState);

/**
 * Custom hook to access the video player state and actions.;
 *
 * @returns {VideoPlayerState} The current state and actions of the video player.
 * @throws {Error} If the hook is not used within a VideoPlayerStateContextProvider.
 */
export function useVideo(): VideoPlayerState {
    const context = useContext(VideoPlayerStateContext);
    if (!context) {
        throw new Error('useVideo must be used within a VideoPlayerStateContextProvider');
    }
    return context;
}

export type VideoPlayerStateContextProviderProps = { children: React.ReactNode; videoKey?: string; useVideoFromArticle?: boolean };

export function VideoPlayerStateContextProvider({ children, videoKey, useVideoFromArticle = true }: VideoPlayerStateContextProviderProps) {
    const externalVideoState = usePlayedVideo();
    const registeredVideoState = useRegisteredVideo(videoKey);

    return (
        <VideoPlayerStateContext.Provider value={useVideoFromArticle ? externalVideoState : registeredVideoState}>
            {children}
        </VideoPlayerStateContext.Provider>
    );
}

/**
 * Custom hook to access the video player state and actions.
 *
 * @returns {VideoPlayerState} The current state and actions of the video player.
 */
export function useRegisteredVideo(videoKey = 'default') {
    return useSessionData('videoState' + videoKey) ?? {};
}

export type SimpleVideoProps = { src: string; controls: boolean; videoKey?: string };

// Simple video component, which uses the video state context
// FIXME: This function is too large and should be broken up
/* eslint-disable-next-line max-lines-per-function */
export function SimpleVideo({ src, controls, videoKey = 'default' }: SimpleVideoProps) {
    const datastore = useDatastore();
    const videoRef = useRef<HTMLVideoElement>(null);

    useEffect(() => {
        if (!videoRef.current) return;
        const video = videoRef.current;

        const updateVideoState = () => {
            const play = () => video.play();
            const pause = () => video.pause();
            const seek = (time) => (video.currentTime = time);

            const playerInfo = {
                currentTime: video.currentTime,
                paused: video.paused,
                ended: video.ended,
                ready: video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA,
                duration: video.duration,
                src: video.src,
                play,
                pause,
                seek,
            };

            datastore.setSessionData('videoState' + videoKey, playerInfo);
        };

        // Event listeners
        video.addEventListener('play', updateVideoState);
        video.addEventListener('pause', updateVideoState);
        video.addEventListener('timeupdate', updateVideoState);
        video.addEventListener('ended', updateVideoState);
        video.addEventListener('canplaythrough', updateVideoState);

        // Initial update
        updateVideoState();

        return () => {
            if (!video) return; // Ensure video exists before removing listeners

            video.removeEventListener('play', updateVideoState);
            video.removeEventListener('pause', updateVideoState);
            video.removeEventListener('timeupdate', updateVideoState);
            video.removeEventListener('ended', updateVideoState);
            video.removeEventListener('canplaythrough', updateVideoState);
        };
    }, [videoRef.current]);

    return (
        <View>
            <video controls={controls} ref={videoRef} src={src}>
                {' '}
            </video>
        </View>
    );
}
